/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from '@chakra-ui/react';
import { TokenManager } from "utils/tokenManager";

const RequestWhitelistAddressRejectConfirmModal: React.FC<{
    dataToReject: any;
    isOpen: boolean;
    onClose(): void;
    triggerReload(): void;
    setSwalProps: any;
}> = ({ isOpen, onClose, triggerReload, dataToReject, setSwalProps }) => {

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [reason, setReason] = useState<string>("");
    const userRole = localStorage.getItem("userRole");

    const closeModal = async () => {
        setReason("");
        onClose();
    };

    const handleSubmit = async () => {
        if (!reason.trim()) {
            setSwalProps({
                show: true,
                icon: "warning",
                title: "Validation Error",
                html: "Please provide a reason for rejection.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                },
            });
            return;
        }

        try {
            setIsProcessing(true);

            const data = {
                username: localStorage.getItem("username"),
                userRole: userRole,
                subaccount_id: dataToReject.subaccount.id,
                wlad_id: dataToReject.wlad_id,
                address: dataToReject.address,
                subaccountName: dataToReject.subaccount.name,
                network: dataToReject.network,
                asset: dataToReject.asset,
                status: "3",
                reason: reason.trim()
            }

            await axios.post(process.env.REACT_APP_API_URL + `/api/${userRole.toLowerCase()}/trade/action/update-whitelist-address-status`, data)
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Whitelist address request has been rejected",
                            showConfirmButton: true,
                            didClose: () => {
                                triggerReload();
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    } else {
                        setIsProcessing(false);
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response.data.message,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const formatDate = (timestamp: string) => {
        if (!timestamp) return "-";
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const formatStatus = (status: string) => {
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const isFormValid = reason.trim() !== "";

    return (
        <>
            {dataToReject &&
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-2xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-red-50 p-2 text-red-600 ring-1 ring-red-100 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-900/30">
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="h-5 w-5"
                                        fill="currentColor"
                                    >
                                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                    </svg>
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Reject Whitelist Address Request
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        This action cannot be undone
                                    </p>
                                </div>
                            </div>

                            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                                You're about to reject this request. This action cannot be undone.
                            </div>

                            <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                                <div className="flex flex-col gap-3 text-sm">
                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Nickname
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToReject.nickname}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Address
                                        </div>
                                        <div className="break-all font-medium text-gray-900 dark:text-white">
                                            {dataToReject.address}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Asset
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToReject.asset}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Network
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToReject.network}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Merchant
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToReject.merchant.name}
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                                            Subaccount
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {dataToReject.subaccount.name}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-5">
                                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                                    Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    disabled={isProcessing}
                                    className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-red-600/0 placeholder:text-gray-400 focus:border-red-600 focus:ring-2 focus:ring-red-600/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white h-28 resize-none"
                                    id="reason"
                                    autoComplete="off"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Enter reason for rejection"
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Provide a clear reason for rejecting this whitelist address request.
                                </p>
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                                >
                                    Not now
                                </Button>

                                <Button
                                    isDisabled={!isFormValid || isProcessing || TokenManager.isImpersonating()}
                                    onClick={() => handleSubmit()}
                                    isLoading={isProcessing}
                                    loadingText='Processing...'
                                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                                >
                                    Reject request
                                </Button>
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            }
        </>
    );
};

export default RequestWhitelistAddressRejectConfirmModal;